import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from 'components/ui/alert-dialog';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useDeleteAllToolActions } from 'features/chat/agent-chat-module/hooks/use-gpt-chat';
import { useToast } from 'hooks/use-toast';

export function ToolApiDeleteAllActionsModal({
  toolId,
  open,
  onOpenChange,
  projectKey,
  isBlocksTool,
}: {
  toolId: string;
  open: boolean;
  onOpenChange: (value: boolean) => void;
  projectKey: string;
  isBlocksTool?: boolean;
}) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useDeleteAllToolActions();

  const handleConfirm = async () => {
    try {
      const response = await mutateAsync({ toolId, projectKey, isBlocksTool });
      if (!response.is_success) {
        throw new Error(
          response.message ||
            response.detail ||
            t('FAILED_TO_DELETE_ALL_ACTIONS') ||
            'Failed to delete tool actions'
        );
      }
      toast({
        variant: 'success',
        title: t('ALL_ACTIONS_DELETED') || 'All actions deleted',
        description:
          response.detail ||
          t('ALL_ACTIONS_DELETED_SUCCESSFULLY') ||
          'All actions deleted successfully.',
      });
      queryClient.invalidateQueries({ queryKey: ['tools', 'detail', toolId] });
      onOpenChange(false);
    } catch (error) {
      toast({
        variant: 'destructive',
        title: t('DELETE_FAILED') || 'Delete failed',
        description:
          error instanceof Error
            ? error.message
            : t('FAILED_TO_DELETE_ALL_ACTIONS') || 'Failed to delete tool actions.',
      });
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('DELETE_ALL_ACTIONS') || 'Delete All Actions'}</AlertDialogTitle>
          <AlertDialogDescription>
            {t('DELETE_ALL_ACTIONS_CONFIRMATION') ||
              'Are you sure you want to delete all APIs for this tool? This cannot be undone.'}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>{t('CANCEL') || 'Cancel'}</AlertDialogCancel>
          <AlertDialogAction onClick={handleConfirm} disabled={isPending}>
            {isPending ? t('DELETING') || 'Deleting...' : t('DELETE_ALL') || 'Delete All'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
